/**
 * 把「回合时间线」接口返回的数据排成可以直接画的泳道。纯计算，不碰 DOM。
 *
 * 图上只画关键节点，全部节点在页面下方的明细表里。每条泳道分两层：
 * - markers：一个时刻。画在泳道顶部的一条线上；挤在一起的点合并成一个，标签写「最重要的那个 +N」。
 * - bars：一段耗时。画在下面的若干行里；时间上重叠的色块自动分到不同的行，互不遮挡。
 */

const LABEL_GAP_PX = 10
const MIN_BAR_PX = 3
const CLUSTER_PX = 14
/** 事件发出后前端超过这么久才处理，才画成一段排队。 */
const QUEUE_MIN_MS = 200
/** 六个指标里的四个「时刻」；另外两个（最长空档、空档合计）是时长。 */
export const MOMENT_METRICS = ['first_feedback', 'npc_first_text', 'npc_first_sound', 'input_unlocked']
/** 玩家看到的这条泳道上，值得画成点的前端节点；其余节点只在明细表里。 */
const SCREEN_MARKS = ['submit', 'text:kp', 'text:npc_action', 'text:deferred', 'stat:flash', 'options:shown', 'clue:toast', 'input_unlocked']

/** 时间轴刻度间隔：时长越长间隔越大，保证刻度数在十个上下。 */
function tickStepMs(durationMs) {
  if (durationMs <= 10_000) return 1000
  if (durationMs <= 30_000) return 2000
  if (durationMs <= 60_000) return 5000
  return 10_000
}

/** 估算 11px 字号下一段文字的宽度：中日韩字符按 11px，其他按 6.4px。 */
export function estimateTextWidth(text) {
  let width = 0
  for (const ch of text) width += /[\u3000-\u9fff\uff00-\uffef]/.test(ch) ? 11 : 6.4
  return width
}

/** 时长：不带 @。 */
export function formatSeconds(ms) {
  return `${(ms / 1000).toFixed(1)}s`
}

/** 时刻：带 @，从玩家点提交算起。页面上所有写时间的地方都按这条约定，时刻和时长才分得开。 */
export function formatMoment(ms) {
  return `@${(ms / 1000).toFixed(1)}s`
}

/** 时间轴的总长：最后一个节点之后留一点余量，再向上取整到刻度。组件用它把容器宽度换算成 scale。 */
export function axisDurationMs(timeline) {
  const lastMs = timeline.nodes.length === 0 ? 1000 : timeline.nodes[timeline.nodes.length - 1].at_ms
  const step = tickStepMs(lastMs)
  return Math.ceil((lastMs + step * 0.15) / step) * step
}

/**
 * @param {object} timeline 接口返回的时间线
 * @param {{ scale: number, label: (key: string, params?: object, fallback?: string) => string }} options
 *   scale 是每毫秒多少像素；label 查不到翻译时返回 fallback
 */
export function layoutTimeline(timeline, { scale, label }) {
  const durationMs = axisDurationMs(timeline)
  const step = tickStepMs(durationMs)
  const ticks = []
  for (let ms = 0; ms <= durationMs; ms += step) ticks.push({ ms, label: `${ms / 1000}s` })

  const lanes = chartLanes(timeline, label, scale)
  for (const lane of lanes) {
    lane.markers = clusterMarkers(lane.markers, scale, label)
    assignRows(lane.markers, scale, item => item.at_ms, item => estimateTextWidth(item.text))
    lane.markerRows = rowCount(lane.markers)
    packBars(lane.bars, scale)
    lane.barRows = rowCount(lane.bars)
  }

  // 四个「时刻」指标画成贯穿全图的竖线；两个「时长」指标（最长空档、空档合计）在卡片里，不画线
  const metricLines = []
  for (const key of MOMENT_METRICS) {
    const metric = timeline.metrics?.[key] ?? null
    if (metric !== null) metricLines.push({ key, at_ms: metric.ms, text: `${label(`metrics.${key}`)} ${formatMoment(metric.ms)}` })
  }
  metricLines.sort((a, b) => a.at_ms - b.at_ms)
  assignRows(metricLines, scale, item => item.at_ms, item => estimateTextWidth(item.text))

  // 空档：铺满「玩家看到的」泳道的色块区；够宽才把秒数写进去
  const gaps = timeline.gaps.map(gap => {
    const text = label('gap', { seconds: formatSeconds(gap.end_ms - gap.start_ms) })
    return {
      ...gap,
      text: (gap.end_ms - gap.start_ms) * scale >= estimateTextWidth(text) + 14 ? text : null,
      title: `${text}\n${gap.before} → ${gap.after}\n@${Math.round(gap.start_ms)}ms → @${Math.round(gap.end_ms)}ms`,
    }
  })

  return { durationMs, ticks, lanes, gaps, metricLines, metricRows: rowCount(metricLines) }
}

/** 图上的五条泳道，去掉空的。 */
function chartLanes(timeline, label, scale) {
  const hasClientSound = timeline.client_marks.some(mark => mark.name.startsWith('tts:') || mark.name.startsWith('audio:'))
  return [
    screenLane(timeline, label),
    soundLane(timeline, label),
    eventsLane(timeline, label),
    parseLane(timeline, label),
    modelLane(timeline, label, scale),
    // 前端已经报了配音的请求、首片、出声、播完时，服务端的合成起止就是重复信息，只留在明细表里
    hasClientSound ? { key: 'tts', markers: [], bars: [] } : ttsLane(timeline, label),
  ].filter(lane => lane.markers.length > 0 || lane.bars.length > 0)
}

/**
 * 玩家看到的：视频段、状态 CG、NPC 文字流式出现是一段；提交、旁白出现、建议按钮、解锁等是一个时刻。
 * 哪些节点配成一段由接口的 perceived_intervals 给出，这里只管怎么画。
 */
function screenLane(timeline, label) {
  const markers = []
  const bars = []
  for (const interval of timeline.perceived_intervals) {
    const video = /^video:(action|speech):(player|npc):started$/.exec(interval.name)
    if (video !== null) {
      bars.push(bar({ tone: 'video', start: interval.start_ms, end: interval.end_ms, name: label(`client.video.${video[1]}.${video[2]}`, undefined, interval.name) }))
    } else if (interval.name === 'state_cg:started') {
      bars.push(bar({ tone: 'cg', start: interval.start_ms, end: interval.end_ms, name: label('client.stateCg', undefined, interval.name) }))
    } else if (interval.name === 'text:npc') {
      if (interval.end_ms > interval.start_ms) bars.push(bar({ tone: 'text', start: interval.start_ms, end: interval.end_ms, name: label('client.textStream', undefined, interval.name) }))
      else markers.push(marker({ tone: 'text', at: interval.start_ms, weight: 3, text: label('client.marks.text_npc', undefined, interval.name), name: interval.name }))
    }
  }
  for (const mark of timeline.client_marks) {
    if (!SCREEN_MARKS.includes(mark.name)) continue
    markers.push(marker({
      tone: mark.name.startsWith('text:') ? 'text' : mark.name === 'submit' || mark.name === 'input_unlocked' ? 'key' : 'hint',
      at: mark.at_ms,
      weight: mark.name === 'submit' || mark.name === 'input_unlocked' ? 3 : 2,
      text: label(`client.marks.${mark.name.replaceAll(':', '_')}`, undefined, mark.name),
      name: mark.name,
    }))
  }
  return { key: 'screen', markers, bars }
}

/**
 * 玩家听到的：每句配音先是一段浅色的等待，再是一段实色的播放（出声 → 播完）。
 * 两句都由后端合成、分片推给前端，等待是后端开始合成 → 第一片发出；前端自己请求合成的（后端放弃时才有）等待是请求 → 首片到手。
 */
function soundLane(timeline, label) {
  const marks = timeline.client_marks
  const bars = []
  for (const actor of ['player', 'npc']) {
    const requested = timeline.server_marks.find(mark => mark.name === `speech:${actor}:requested`)
    const firstChunk = timeline.server_marks.find(mark => mark.name === `emit:speech_chunk:${actor}`)
    if (requested !== undefined && firstChunk !== undefined) {
      bars.push(bar({ tone: `audio-${actor}`, light: true, start: requested.at_ms, end: firstChunk.at_ms, name: label(`audio.waiting.${actor}`, undefined, requested.name) }))
    }
  }
  for (const [index, mark] of marks.entries()) {
    const later = marks.slice(index + 1)
    const requested = /^tts:(player|npc):requested$/.exec(mark.name)
    if (requested !== null) {
      const ready = later.find(item => item.name === `tts:${requested[1]}:ready`)
      // 配音已经在缓存里时请求和到手是同一刻，不是一段等待
      if (ready !== undefined && ready.at_ms - mark.at_ms >= QUEUE_MIN_MS) bars.push(bar({ tone: `audio-${requested[1]}`, light: true, start: mark.at_ms, end: ready.at_ms, name: label(`audio.waiting.${requested[1]}`, undefined, mark.name) }))
    }
  }
  for (const interval of timeline.perceived_intervals) {
    const started = /^audio:(player|npc):started$/.exec(interval.name)
    if (started !== null) bars.push(bar({ tone: `audio-${started[1]}`, start: interval.start_ms, end: interval.end_ms, name: label(`audio.playing.${started[1]}`, undefined, interval.name) }))
  }
  return { key: 'sound', markers: [], bars }
}

/**
 * 后端事件：后端每发出一个事件是一个点。前端因为在播视频而晚处理的事件，从发出到开始处理画成一段排队。
 * 回合开始、前置完成、开始落库这类内部步骤只在明细表里。
 */
function eventsLane(timeline, label) {
  const markers = []
  const bars = []
  for (const mark of timeline.server_marks) {
    if (!mark.name.startsWith('emit:')) continue
    const event = mark.name.slice('emit:'.length)
    const eventLabel = label(`events.${event.replaceAll(':', '_')}`, undefined, event)
    markers.push(marker({ tone: 'emit', at: mark.at_ms, weight: event === 'turn_playback_plan' ? 1 : 2, text: label('server.emitted', { event: eventLabel }, mark.name), name: mark.name }))
    // 播放计划和 NPC 回应是同一刻发出的，排队也是同一段；只画 NPC 回应那一条
    if (event === 'turn_playback_plan') continue
    const received = timeline.client_marks.find(item => item.name === `recv:${event}`)
    if (received !== undefined && received.at_ms - mark.at_ms >= QUEUE_MIN_MS) {
      bars.push(bar({ tone: 'queue', start: mark.at_ms, end: received.at_ms, name: label('server.queued', { event: eventLabel }, mark.name) }))
    }
  }
  return { key: 'events', markers, bars }
}

/**
 * 模型输出解析：两次调用的输出都边生成边解析。KP 的 NPC 所需字段一齐全就叫 NPC，不等动态剧情字段；
 * NPC 首 token 到手后表演状态、旁白、台词依次解析出来，台词一完整就起配音、发播放阶段。
 * 浅色的两段是「其余字段还在生成」：不流式的话要等到那时才能往下走，流式提前了多少，看它。
 */
function parseLane(timeline, label) {
  const bars = []
  const at = name => timeline.server_marks.find(mark => mark.name === name)?.at_ms
  // KP：首 token 到 NPC 所需字段齐全是一段，NPC 在这一刻叫起来；之后到 KP 调用结束是只给动态剧情用的尾巴
  const kpCall = timeline.model_calls.find(call => call.role === 'kp')
  const kpFirstToken = kpCall?.attempts[kpCall.attempts.length - 1]?.first_token_ms ?? null
  const kpCoreDone = at('kp_stream:core_done')
  if (kpFirstToken !== null && kpCoreDone !== undefined) {
    bars.push(bar({ tone: 'parse-kp-core', start: kpFirstToken, end: kpCoreDone, name: label('parse.kp_core', undefined, 'kp core') }))
    if (kpCall.end_ms > kpCoreDone) bars.push(bar({ tone: 'parse-rest', light: true, start: kpCoreDone, end: kpCall.end_ms, name: label('parse.kp_rest', undefined, 'kp rest') }))
  }
  const npcCall = timeline.model_calls.find(call => call.role === 'npc')
  const lastAttempt = npcCall === undefined ? undefined : npcCall.attempts[npcCall.attempts.length - 1]
  const firstToken = lastAttempt?.first_token_ms ?? null
  const stateParsed = at('npc_stream:expression_state')
  if (firstToken !== null && stateParsed !== undefined) {
    // 首 token 的时刻来自模型调用日志，和字段流的打点差几毫秒，表演状态是第一个字段，别画成负长度
    bars.push(bar({ tone: 'parse-state', start: Math.min(firstToken, stateParsed), end: stateParsed, name: label('parse.expression_state', undefined, 'expression_state') }))
  }
  for (const field of ['narration', 'npcLine']) {
    const first = at(`npc_stream:${field}:first`)
    const done = at(`npc_stream:${field}:done`)
    if (first !== undefined && done !== undefined) bars.push(bar({ tone: `parse-${field}`, start: first, end: done, name: label(`parse.${field}`, undefined, field) }))
  }
  const lineDone = at('npc_stream:npcLine:done')
  if (npcCall !== undefined && lineDone !== undefined && npcCall.end_ms > lineDone) {
    bars.push(bar({ tone: 'parse-rest', light: true, start: lineDone, end: npcCall.end_ms, name: label('parse.rest', undefined, 'rest') }))
  }
  return { key: 'parse', markers: [], bars }
}

/**
 * 模型调用：每次尝试拆成「等首 token」和「生成」两段，秒数写在色块里，放不下的写进上方标签。
 * 发生过重发时每次尝试各画一组。
 */
function modelLane(timeline, label, scale) {
  const bars = []
  for (const call of timeline.model_calls) {
    const role = label(`roles.${call.role}`, undefined, call.role)
    if (call.attempts.length === 0) {
      bars.push(bar({ tone: `model-${roleTone(call.role)}`, start: call.start_ms, end: call.end_ms, name: role }))
      continue
    }
    for (const [index, attempt] of call.attempts.entries()) {
      const name = call.attempts.length > 1 ? `${role} #${index + 1}` : role
      const firstToken = attempt.first_token_ms ?? attempt.end_ms
      const waitText = label('model.wait', { seconds: formatSeconds(firstToken - attempt.start_ms) })
      const generateText = label('model.generate', { seconds: formatSeconds(attempt.end_ms - firstToken) })
      const waitFits = (firstToken - attempt.start_ms) * scale >= estimateTextWidth(waitText) + 12
      const generateFits = (attempt.end_ms - firstToken) * scale >= estimateTextWidth(generateText) + 12
      const item = bar({ tone: `model-${roleTone(call.role)}`, start: attempt.start_ms, end: attempt.end_ms, name })
      item.split_ms = firstToken
      item.wait_text = waitFits ? waitText : null
      item.generate_text = generateFits ? generateText : null
      item.label = [
        `${name} ${formatSeconds(attempt.end_ms - attempt.start_ms)}`,
        ...(waitFits ? [] : [waitText]),
        ...(generateFits ? [] : [generateText]),
        label('model.tokens', { tokens: attempt.completion_tokens ?? '—' }),
      ].join(' · ')
      item.detail = `${waitText} · ${generateText}`
      item.title = `${name} · ${waitText} · ${generateText}\n@${Math.round(attempt.start_ms)}ms → @${Math.round(attempt.end_ms)}ms`
      bars.push(item)
    }
  }
  return { key: 'model', markers: [], bars }
}

function roleTone(role) {
  switch (role) {
    case 'kp': return 'kp'
    case 'npc': return 'npc'
    case 'npc_action_kp': return 'action'
    default: return 'other'
  }
}

function ttsLane(timeline, label) {
  return {
    key: 'tts',
    markers: [],
    bars: timeline.tts_calls.map(call => bar({ tone: 'tts', start: call.start_ms, end: call.end_ms, name: label(`tts.${call.actor_type}`, undefined, `TTS ${call.actor_type}`) })),
  }
}

/** 色块的标签默认是「名字 + 时长」，画在色块上方；detail 是名字之外的补充说明，只有模型调用有。 */
function bar({ tone, light = false, start, end, name }) {
  const label = `${name} ${formatSeconds(end - start)}`
  return { tone, light, start_ms: start, end_ms: end, split_ms: null, wait_text: null, generate_text: null, name, detail: null, label, title: `${label}\n@${Math.round(start)}ms → @${Math.round(end)}ms` }
}

function marker({ tone, at, weight, text, name }) {
  return { tone, at_ms: at, weight, text, names: [`${name} @ ${Math.round(at)}ms`] }
}

/** 挤在一起的点合并成一个：标签取最重要的那个，后面写 +N；悬停能看到合并进来的全部节点。 */
function clusterMarkers(markers, scale, label) {
  const sorted = [...markers].sort((a, b) => a.at_ms - b.at_ms)
  const clusters = []
  for (const item of sorted) {
    const last = clusters[clusters.length - 1]
    if (last !== undefined && (item.at_ms - last.first_ms) * scale < CLUSTER_PX) last.items.push(item)
    else clusters.push({ first_ms: item.at_ms, items: [item] })
  }
  return clusters.map(cluster => {
    let lead = cluster.items[0]
    for (const item of cluster.items) {
      if (item.weight > lead.weight) lead = item
    }
    const extra = cluster.items.length - 1
    return {
      tone: lead.tone,
      at_ms: lead.at_ms,
      text: extra === 0 ? lead.text : label('clustered', { text: lead.text, count: extra }, lead.text),
      title: cluster.items.flatMap(item => item.names).join('\n'),
    }
  })
}

/** 时间上重叠（或标签会压住后一个色块）的色块分到不同的行。 */
function packBars(bars, scale) {
  bars.sort((a, b) => a.start_ms - b.start_ms)
  const rowEnds = []
  for (const item of bars) {
    const x = item.start_ms * scale
    item.width_px = Math.max(MIN_BAR_PX, (item.end_ms - item.start_ms) * scale)
    const right = x + Math.max(item.width_px, estimateTextWidth(item.label))
    let row = rowEnds.findIndex(end => x >= end + LABEL_GAP_PX)
    if (row === -1) row = rowEnds.length
    rowEnds[row] = right
    item.row = row
  }
}

/** 从左到右，每个标签落到第一个放得下的行。 */
function assignRows(items, scale, startOf, widthOf) {
  const rowEnds = []
  for (const item of items) {
    const x = startOf(item) * scale
    let row = rowEnds.findIndex(end => x >= end + LABEL_GAP_PX)
    if (row === -1) row = rowEnds.length
    rowEnds[row] = x + widthOf(item)
    item.row = row
  }
}

function rowCount(items) {
  return items.reduce((rows, item) => Math.max(rows, item.row + 1), 0)
}

/**
 * 时间花在哪：图上每个色块（视频、配音、模型调用、TTS、排队）加上每段空档，按时长从长到短取前几个。
 * 只看色块和空档，不看相邻节点的间隔——两个来源不同的节点之间不是一个步骤，它们隔多久说明不了什么。
 * 这里不用色块里的字，scale 无所谓，传 0。
 */
export function longestSpans(timeline, label, count) {
  const spans = []
  for (const lane of chartLanes(timeline, label, 0)) {
    for (const item of lane.bars) spans.push({ lane: lane.key, name: item.name, detail: item.detail, start_ms: item.start_ms, end_ms: item.end_ms })
  }
  for (const gap of timeline.gaps) {
    spans.push({ lane: 'idle', name: `${gap.before} → ${gap.after}`, detail: null, start_ms: gap.start_ms, end_ms: gap.end_ms })
  }
  spans.sort((a, b) => (b.end_ms - b.start_ms) - (a.end_ms - a.start_ms))
  return spans.slice(0, count)
}
